import React, { useEffect, useState } from 'react';
import { API } from '../api';
import { defaultProductPicture } from '../imageUtils';
import {
  addPendingReceipt,
  cacheProductUnits,
  getAll,
  getCachedProductUnits,
  putMany,
  searchCached
} from '../offlineDb';
import { syncPendingReceipts } from '../offlineSync';

export default function CreateReceipt({ token }) {
  const [farmers, setFarmers] = useState([]);
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);

  const [farmerQuery, setFarmerQuery] = useState('');
  const [productQuery, setProductQuery] = useState('');

  const [farmer, setFarmer] = useState(null);
  const [product, setProduct] = useState(null);
  const [unit, setUnit] = useState('');
  const [quantity, setQuantity] = useState('');

  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      if (!farmerQuery) return;
      fetch(`${API}/farmers/search?q=${encodeURIComponent(farmerQuery)}`, { headers: { Authorization: token } })
        .then((r) => r.json())
        .then((data) => {
          setFarmers(data);
          putMany('farmers', data);
        })
        .catch(async () => setFarmers(searchCached(await getAll('farmers'), farmerQuery)));
    }, 300);
    return () => clearTimeout(t);
  }, [farmerQuery, token]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!productQuery) return;
      fetch(`${API}/products/search?q=${encodeURIComponent(productQuery)}`, { headers: { Authorization: token } })
        .then((r) => r.json())
        .then((data) => {
          setProducts(data);
          putMany('products', data);
        })
        .catch(async () => setProducts(searchCached(await getAll('products'), productQuery)));
    }, 300);
    return () => clearTimeout(t);
  }, [productQuery, token]);

  useEffect(() => {
    if (!product) {
      setUnits([]);
      setUnit('');
      return;
    }

    fetch(`${API}/products/${product.id}/units`, { headers: { Authorization: token } })
      .then((r) => r.json())
      .then((data) => {
        setUnits(data);
        cacheProductUnits(product.id, data);
      })
      .catch(async () => {
        const cachedUnits = await getCachedProductUnits(product.id);
        setUnits(cachedUnits);
        if (!cachedUnits.length) setError('Could not load units for this product.');
      });
  }, [product, token]);

  const resetForm = () => {
    setFarmer(null);
    setProduct(null);
    setUnit('');
    setQuantity('');
    setFarmerQuery('');
    setProductQuery('');
  };

  const savePendingReceipt = async (clientId, clientCreatedAt) => {
    const unitRecord = units.find((item) => item.id === unit);
    const pending = {
      id: clientId,
      status: 'pending_sync',
      client_created_at: clientCreatedAt,
      payload: {
        client_receipt_id: clientId,
        client_created_at: clientCreatedAt,
        farmer_id: farmer.id,
        product_id: product.id,
        unit_id: unit,
        quantity
      },
      preview: {
        id: clientId,
        farmer_id: farmer.id,
        farmer_name: farmer.name,
        product_id: product.id,
        product_name: product.name,
        product_reference: product.reference_number,
        product_picture: product.picture,
        unit_id: unit,
        unit_name: unitRecord?.name,
        quantity,
        timestamp: clientCreatedAt
      }
    };
    await addPendingReceipt(pending);
    window.dispatchEvent(new Event('offline-receipts-updated'));
    return pending;
  };

  const createReceipt = async () => {
    setLoading(true);
    setError('');
    const clientId = crypto.randomUUID();
    const clientCreatedAt = new Date().toISOString();

    try {
      const res = await fetch(`${API}/receipts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({
          client_receipt_id: clientId,
          client_created_at: clientCreatedAt,
          farmer_id: farmer.id,
          product_id: product.id,
          unit_id: unit,
          quantity
        })
      });
      if (!res.ok) {
        const error = new Error(await res.text());
        error.serverResponse = true;
        throw error;
      }
      setCreated(await res.json());
      setConfirm(false);
    } catch (err) {
      if (err.serverResponse) {
        setError('Could not create receipt. Check the backend and selected fields.');
        return;
      }
      const pending = await savePendingReceipt(clientId, clientCreatedAt);
      setCreated({ pending: true, receipt: pending.preview });
      setConfirm(false);
      syncPendingReceipts(token);
    } finally {
      setLoading(false);
    }
  };

  if (created) {
    return (
      <div className="container">
        <h2>{created.pending ? 'Receipt Pending Sync' : 'Receipt Created'}</h2>
        <p><b>Farmer:</b> {created.receipt.farmer_name}</p>
        <p><b>Product:</b> {created.receipt.product_name}</p>
        {created.receipt.product_reference && <p><b>Reference:</b> {created.receipt.product_reference}</p>}
        <p><b>Quantity:</b> {created.receipt.quantity} {created.receipt.unit_name}</p>
        <div className="receiptImagePair">
          <img className="productImageLarge" src={created.receipt.product_picture || defaultProductPicture} alt="" />
          {!created.pending && <img className="qrImageLarge" src={created.receipt.qr} alt={`QR code for receipt ${created.receipt.id}`} />}
        </div>
        {created.pending && <p className="pendingBadge">Pending sync - not official until validated by server.</p>}
        {!created.pending && (
          <>
            <p><b>Receipt ID:</b></p>
            <p className="mono">{created.receipt.id}</p>
            <p><b>Hash:</b></p>
            <p className="hashText">{created.receipt.hash}</p>
            <a href={created.verifyUrl} target="_blank" rel="noreferrer">Open verification page</a>
            <a href={`${API}/receipt/${created.receipt.id}/pdf`} target="_blank" rel="noreferrer">Download PDF</a>
          </>
        )}
        <button onClick={() => {
          setCreated(null);
          resetForm();
        }}>Create Another Receipt</button>
      </div>
    );
  }

  if (confirm) {
    const unitName = units.find((item) => item.id === unit)?.name;
    return (
      <div className="container">
        <h2>Confirm Receipt</h2>
        <p><b>Farmer:</b> {farmer.name}</p>
        <p><b>Product:</b> {product.name}</p>
        <p><b>Quantity:</b> {quantity} {unitName}</p>
        <button disabled={loading} onClick={createReceipt}>{loading ? 'Creating...' : 'Confirm'}</button>
        {error && <p className="error">{error}</p>}
        <button onClick={() => setConfirm(false)}>Cancel</button>
      </div>
    );
  }

  return (
    <div className="container">
      <h2>Create Receipt</h2>
      {error && <p className="error">{error}</p>}

      {farmer && <p>{farmer.name}</p>}
      {product && <p>{product.name}</p>}
      {unit && <p>{units.find((item) => item.id === unit)?.name}</p>}

      <input placeholder="Search farmer" value={farmerQuery} onChange={(e) => setFarmerQuery(e.target.value)} />
      {farmers.map((item) => (
        <div
          className={`card ${item.blocked_at ? 'disabledCard' : ''}`}
          key={item.id}
          onClick={() => {
            if (item.blocked_at) {
              setError('This farmer is currently blocked. No new receipt can be created for this farmer.');
              return;
            }
            setError('');
            setFarmer(item);
            setFarmerQuery(item.name);
            setFarmers([]);
          }}
        >
          <span>{item.name}</span>
          {item.registration_number && <small>{item.registration_number}</small>}
          {item.blocked_at && <small className="blockedText">Blocked - receipts disabled</small>}
        </div>
      ))}

      <input placeholder="Search product" value={productQuery} onChange={(e) => setProductQuery(e.target.value)} />
      {products.map((item) => (
        <div className="card searchResult" key={item.id} onClick={() => {
          setProduct(item);
          setProductQuery(item.name);
          setProducts([]);
          setUnit('');
        }}>
          <img className="productThumb" src={item.picture || defaultProductPicture} alt="" />
          <span>{item.name}</span>
          {item.reference_number && <small>{item.reference_number}</small>}
        </div>
      ))}

      <select value={unit} onChange={(e) => setUnit(e.target.value)} disabled={!product}>
        <option value="">{product ? 'Select unit' : 'Select product first'}</option>
        {units.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
      </select>
      {product && units.length === 0 && <p>No units have been configured for this product.</p>}

      <input placeholder="Quantity" value={quantity} onChange={(e) => setQuantity(e.target.value)} />

      <button onClick={() => {
        if (!farmer || !product || !unit || !quantity) {
          setError('Choose a farmer, product, unit, and quantity first.');
          return;
        }
        setError('');
        setConfirm(true);
      }} disabled={loading}>Create</button>
    </div>
  );
}
